"use client";

import { Button, Modal } from "@carbon/react";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

function ModalPortal({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setTarget(document.body);
  }, []);
  if (!target) {
    return null;
  }
  return createPortal(children, target);
}

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  danger?: boolean;
  size?: "sm" | "md" | "lg";
  primaryLabel: string;
  secondaryLabel: string;
  primaryDisabled?: boolean;
  busy?: boolean;
  focusSelector?: string;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
};

export function ConfirmDialog({
  open,
  title,
  danger = false,
  size = "sm",
  primaryLabel,
  secondaryLabel,
  primaryDisabled = false,
  busy = false,
  focusSelector,
  onClose,
  onSubmit,
  children,
}: ConfirmDialogProps) {
  return (
    <ModalPortal>
      <Modal
        open={open}
        danger={danger}
        size={size}
        modalHeading={title}
        primaryButtonText={primaryLabel}
        secondaryButtonText={secondaryLabel}
        primaryButtonDisabled={primaryDisabled}
        selectorPrimaryFocus={focusSelector}
        onRequestClose={() => {
          if (!busy) {
            onClose();
          }
        }}
        onRequestSubmit={() => {
          if (!primaryDisabled) {
            onSubmit();
          }
        }}
      >
        {children}
      </Modal>
    </ModalPortal>
  );
}

type CreateDialogProps = {
  openLabel: string;
  title: string;
  pending?: boolean;
  open: boolean;
  size?: "sm" | "md" | "lg";
  hideTrigger?: boolean;
  focusSelector?: string;
  onOpen: () => void;
  onClose: () => void;
  children: ReactNode;
};

export function CreateDialog({
  openLabel,
  title,
  pending = false,
  open,
  size = "sm",
  hideTrigger = false,
  focusSelector,
  onOpen,
  onClose,
  children,
}: CreateDialogProps) {
  return (
    <>
      {hideTrigger ? null : (
        <Button type="button" kind="tertiary" size="md" onClick={onOpen}>
          {openLabel}
        </Button>
      )}
      <ModalPortal>
        <Modal
          open={open}
          passiveModal
          size={size}
          modalHeading={title}
          selectorPrimaryFocus={focusSelector}
          onRequestClose={() => {
            if (!pending) {
              onClose();
            }
          }}
        >
          {children}
        </Modal>
      </ModalPortal>
    </>
  );
}
